import { Injectable, inject } from '@angular/core';
import { Stage, Layer, Hotspot } from 'marzipano';
import { LoggingService } from '@newroom-connect/library/services';

/**
 * Layer indices for different hotspot types.
 * Higher indices render on top of lower indices.
 */
export enum MarzipanoLayerIndex {
  BASE_SCENE = 0,        // Cubemap layer (managed by Marzipano)
  BASE_HOTSPOTS = 1,     // Content hotspots
  DRAG_OVERLAYS = 2,     // Drag interaction overlays
  TRANSFORM_OVERLAYS = 3, // Transform control overlays
  UI_FEEDBACK = 4        // Selection indicators, etc.
}

/**
 * Interface for layer management operations.
 */
export interface IMarzipanoLayerManager {
  initialize(stage: Stage): void;
  assignHotspotToLayer(hotspot: Hotspot, layerIndex: MarzipanoLayerIndex): void;
  moveHotspotToLayer(hotspot: Hotspot, fromLayer: MarzipanoLayerIndex, toLayer: MarzipanoLayerIndex): void;
  removeHotspotFromLayer(hotspot: Hotspot, layerIndex: MarzipanoLayerIndex): void;
  reorderLayers(): void;
  dispose(): void;
}

/**
 * Manages Marzipano layers for proper hotspot z-ordering.
 * Replaces CSS z-index with native Marzipano layer management.
 */
@Injectable({
  providedIn: 'root'
})
export class MarzipanoLayerService implements IMarzipanoLayerManager {
  private readonly logger = inject(LoggingService);

  private stage: Stage | null = null;
  private layers = new Map<MarzipanoLayerIndex, Layer>();
  private hotspotLayerMap = new Map<Hotspot, MarzipanoLayerIndex>();

  /**
   * Initialize the layer manager with a Marzipano stage.
   *
   * @param stage - The Marzipano stage instance.
   */
  public initialize(stage: Stage): void {
    this.stage = stage;
    this.logger.debug('MarzipanoLayerService initialized with stage');
  }

  /**
   * Assign a hotspot to a specific layer index with custom z-index ordering.
   * This tracks the hotspot's logical layer for ordering purposes.
   *
   * @param hotspot
   * @param layerIndex - The target layer index.
   * @param zIndex - Custom z-index for ordering within the layer (higher = on top).
   */
  public assignHotspotToLayer(hotspot: Hotspot, layerIndex: MarzipanoLayerIndex, zIndex?: number): void {
    try {
      // Track the hotspot's layer assignment
      this.hotspotLayerMap.set(hotspot, layerIndex);

      // Apply layer-based styling to the hotspot's DOM element
      this.applyLayerStyling(hotspot, layerIndex, zIndex);

      this.logger.debug('Hotspot assigned to layer', { layerIndex, zIndex });
    } catch (error) {
      this.logger.error('Failed to assign hotspot to layer', error as Error);
    }
  }

  /**
   * Move a hotspot from one layer to another.
   *
   * @param hotspot
   * @param fromLayer - The source layer index.
   * @param toLayer - The target layer index.
   */
  public moveHotspotToLayer(hotspot: Hotspot, fromLayer: MarzipanoLayerIndex, toLayer: MarzipanoLayerIndex): void {
    try {
      // Update the hotspot's layer assignment
      this.hotspotLayerMap.set(hotspot, toLayer);

      // Apply new layer styling
      this.applyLayerStyling(hotspot, toLayer);

      this.logger.debug('Hotspot moved between layers', { fromLayer, toLayer });
    } catch (error) {
      this.logger.error('Failed to move hotspot between layers', error as Error);
    }
  }

  /**
   * Remove a hotspot from its assigned layer.
   *
   * @param hotspot - The hotspot to remove.
   * @param layerIndex - The layer index to remove from.
   */
  public removeHotspotFromLayer(hotspot: Hotspot, layerIndex: MarzipanoLayerIndex): void {
    try {
      // Remove from tracking
      this.hotspotLayerMap.delete(hotspot);

      // Remove layer-specific styling
      this.removeLayerStyling(hotspot);

      this.logger.debug('Hotspot removed from layer', { layerIndex });
    } catch (error) {
      this.logger.error('Failed to remove hotspot from layer', error as Error);
    }
  }

  /**
   * Reorder all layers to ensure proper z-index hierarchy.
   * This applies consistent z-index values based on layer indices.
   */
  public reorderLayers(): void {
    if (!this.stage) {
      this.logger.warn('Cannot reorder layers: stage not initialized');
      return;
    }

    try {
      // Apply z-index values to all tracked hotspots based on their layer assignments
      for (const [hotspot, layerIndex] of this.hotspotLayerMap.entries()) {
        this.applyLayerStyling(hotspot, layerIndex);
      }

      this.logger.debug('Layers reordered successfully');
    } catch (error) {
      this.logger.error('Failed to reorder layers', error as Error);
    }
  }

  /**
   * Dispose of the layer manager and clean up resources.
   */
  public dispose(): void {
    try {
      // Clear all tracking maps
      this.hotspotLayerMap.clear();
      this.layers.clear();
      this.stage = null;

      this.logger.debug('MarzipanoLayerService disposed');
    } catch (error) {
      this.logger.error('Error during layer service disposal', error as Error);
    }
  }

  /**
   * Apply layer-specific styling to a hotspot's DOM element.
   * This replaces manual z-index management with consistent layer-based values.
   *
   * @param hotspot - The hotspot to style.
   * @param layerIndex - The layer index to apply.
   * @param customZIndex - Custom z-index for ordering within the layer (higher = on top).
   */
  private applyLayerStyling(hotspot: Hotspot, layerIndex: MarzipanoLayerIndex, customZIndex?: number): void {
    try {
      const domElement = hotspot.domElement();

      if (!domElement) {
        this.logger.warn('Cannot apply layer styling: hotspot has no DOM element');
        return;
      }

      // Calculate z-index based on layer index and custom z-index
      const baseZIndex = this.calculateZIndexForLayer(layerIndex, customZIndex);

      // Apply the z-index
      domElement.style.zIndex = baseZIndex.toString();
      domElement.style.position = domElement.style.position || 'relative';

      // Add layer-specific CSS class for debugging and styling
      domElement.classList.add(`marzipano-layer-${layerIndex}`);

      this.logger.debug('Layer styling applied', { layerIndex, customZIndex, finalZIndex: baseZIndex });
    } catch (error) {
      this.logger.error('Failed to apply layer styling', error as Error);
    }
  }

  /**
   * Remove layer-specific styling from a hotspot's DOM element.
   *
   * @param hotspot - The hotspot to clean up.
   */
  private removeLayerStyling(hotspot: Hotspot): void {
    try {
      const domElement = hotspot.domElement();

      if (!domElement) {
        return;
      }

      // Remove layer-specific classes
      for (const layerIndex of Object.values(MarzipanoLayerIndex)) {
        if (typeof layerIndex === 'number') {
          domElement.classList.remove(`marzipano-layer-${layerIndex}`);
        }
      }

      this.logger.debug('Layer styling removed from hotspot');
    } catch (error) {
      this.logger.error('Failed to remove layer styling', error as Error);
    }
  }

  /**
   * Calculate the z-index value for a given layer index with custom ordering.
   * Uses the customZIndex where higher numbers = higher z-index (on top).
   *
   * @param layerIndex - The base layer index.
   * @param customZIndex - Custom z-index for ordering (higher = on top).
   *
   * @returns The calculated z-index value.
   */
  private calculateZIndexForLayer(layerIndex: MarzipanoLayerIndex, customZIndex?: number): number {
    // Get base z-index for the layer type
    const layerZIndexMap: Record<MarzipanoLayerIndex, number> = {
      [MarzipanoLayerIndex.BASE_SCENE]: 0,        // Scene background
      [MarzipanoLayerIndex.BASE_HOTSPOTS]: 1000,   // Content hotspots
      [MarzipanoLayerIndex.DRAG_OVERLAYS]: 2000,   // Drag interactions
      [MarzipanoLayerIndex.TRANSFORM_OVERLAYS]: 3000, // Transform controls
      [MarzipanoLayerIndex.UI_FEEDBACK]: 4000      // UI feedback
    };

    const baseZIndex = layerZIndexMap[layerIndex] || 0;

    // If customZIndex is provided, add it to the base (higher customZIndex = higher final z-index)
    if (customZIndex !== undefined) {
      return baseZIndex + customZIndex;
    }

    return baseZIndex;
  }
}
